import { Badge, BottomNavigation, BottomNavigationAction, Icon } from "@mui/material";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAppSelector } from "~/hooks/reduxHooks";
import { navDestinations } from "~/locationMaps/pageMappings";
import { isFailedOperation, isPendingOperation } from "~/store/offlineQueueSlice";

const HISTORY_PATH = "/mobile/history";

/**
 * Module-level bottom navigation for the inventory shell. Renders one destination per nav entry in
 * the page-mapping registry (Finish / Material / Retail / Activity). The Activity destination
 * carries a badge reflecting offline work — a count of failed writes, or a dot for pending sync.
 */
const InventoryBottomNav = () => {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const operations = useAppSelector((state) => state.offlineQueue.operations);

	const { failedCount, hasPending } = useMemo(() => {
		const pending = operations.filter(isPendingOperation);
		const failed = pending.filter(isFailedOperation).length;
		return { failedCount: failed, hasPending: pending.length > 0 };
	}, [operations]);

	const activeDestination = navDestinations.find((dest) => pathname.startsWith(dest.path))?.path ?? false;

	const renderIcon = (path: string, icon: string) => {
		if (path !== HISTORY_PATH) return <Icon>{icon}</Icon>;
		if (failedCount > 0) {
			return (
				<Badge badgeContent={failedCount} color="error" max={99}>
					<Icon>{icon}</Icon>
				</Badge>
			);
		}
		return (
			<Badge color="info" invisible={!hasPending} overlap="circular" variant="dot">
				<Icon>{icon}</Icon>
			</Badge>
		);
	};

	return (
		<BottomNavigation
			onChange={(_event, value: string) => navigate(value)}
			showLabels
			sx={{ pb: "env(safe-area-inset-bottom)" }}
			value={activeDestination}
		>
			{navDestinations.map((dest) => (
				<BottomNavigationAction
					key={dest.path}
					icon={renderIcon(dest.path, dest.icon)}
					label={dest.label}
					sx={{ minWidth: 64 }}
					value={dest.path}
				/>
			))}
		</BottomNavigation>
	);
};

export default InventoryBottomNav;
